import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { statusLabel, feRetentionLabel } from "@/lib/saleStatus";
import { nameProblem, autoFixName } from "@/lib/names";
import { listSalesLedger, listSalePayments, createSale, getSalePoints, getProgrammePrice, updateSale, deleteSale, requestSaleDeletion, listGymSaleDeletions, listCustomers, getProgrammeCatalog, getSaleTypes, getRevenueItems, quickAddSaleType, getPaymentFees, getMerchStock, merchKey, api } from "@/lib/api";
import { taxIncluded, numberLocale } from "@/lib/money";
import BillButton from "@/components/BillButton";
import ConfirmDetails from "@/components/ConfirmDetails";
import BillHistory from "@/components/BillHistory";
import SalePayments from "@/components/SalePayments";
import { PaymentsLedger } from "@/pages/Sales";
import { atLeast } from "@/lib/roles";
import TaxSplit from "@/components/TaxSplit";
import { ALL_SALES_PROGRAMMES, autoEndDate } from "@/lib/programmes";
import { usePaymentModes } from "@/lib/paymentModes";
import { PERIOD_OPTIONS, rowInPeriod, todayLocal, monthRangeOf, monthLabelOf } from "@/lib/periods";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { RosterSelect } from "@/components/RosterAutocomplete";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Search, Pencil, Bookmark, History, UserSearch, Check, ChevronDown } from "lucide-react";
import { AuditHistoryDialog } from "@/components/AuditHistoryDialog";
import EmailTypoHint from "@/components/EmailTypoHint";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
// Fallback list — actual options come from GET /api/sale-types (settings-managed).
// FE-PIF and PE removed (Feb 2026) — new-customer entries only come via Front End Sales.
const DEFAULT_TYPE_OPTS = ["PIF", "PT", "SPT", "Day Pass", "Pro", "Tshirt", "Event", "Other"];
// "New" is intentionally excluded — every new customer is entered through New Sales (Front End) first;
// their companion All Sales row is created automatically with status='New'.
const STATUS_OPTS = ["FE Conversion", "Renewal", "Pending Payment", "Upsell", "Exit Return"];
// An existing member doing the 6 Week Challenge again: no consult, no sales rep (no incentive),
// 15% off, and their PIF membership pauses for the six weeks (server: _pause_for_6wc).
const EXISTING_MEMBER = "Existing Member";
// Sales (money) fields a consult's sale takes from its consult and payments, never from this form.
const CONSULT_MONEY_FIELDS = ["paid", "gst", "platform_fee_deducted", "net_received", "pending_payment", "payment_mode", "payment_type", "split_payments", "price"];
const PROG_OPTS = ALL_SALES_PROGRAMMES;

// Column filters on the All Sales list (CR-0037). Each reads the value the list shows.
const ALL = "__all";
const BLANK = "__blank";
const COLUMN_FILTERS = [
  { key: "type", label: "Type", get: (r) => r.fe_pt_pe },
  { key: "status", label: "Status", get: (r) => statusLabel(r.status, r._sale) },
  { key: "programme", label: "Programme", get: (r) => r.programme || r.item },
  { key: "mode", label: "Pay mode", get: (r) => r.payment_mode, blank: true },
  { key: "by", label: "By", get: (r) => r.payment_received_by },
];
const NO_COLUMN_FILTERS = Object.fromEntries(COLUMN_FILTERS.map((c) => [c.key, ALL]));
const COL_FILTERS_KEY = "allSales.columnFilters";
const loadColumnFilters = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(COL_FILTERS_KEY) || "{}");
    const out = { ...NO_COLUMN_FILTERS };
    COLUMN_FILTERS.forEach((c) => { if (typeof saved[c.key] === "string" && saved[c.key]) out[c.key] = saved[c.key]; });
    return out;
  } catch { return { ...NO_COLUMN_FILTERS }; }
};
const colVal = (c, r) => String(c.get(r) ?? "").trim();

// Add N months to a YYYY-MM-DD date, clamping the day when the target month is shorter.
function addMonthsISO(iso, months) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const targetMonth = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonth / 12);
  const normMonth = ((targetMonth % 12) + 12) % 12;
  const day = d.getDate();
  const lastDayOfTarget = new Date(targetYear, normMonth + 1, 0).getDate();
  const finalDay = Math.min(day, lastDayOfTarget);
  const dt = new Date(targetYear, normMonth, finalDay);
  return dt.toISOString().slice(0, 10);
}

// Build N equal instalments starting today, spaced 1 month apart.
function buildEqualInstalments(total, count, startISO, mode) {
  const cnt = Math.max(1, Math.min(3, Number(count) || 1));
  const start = startISO || new Date().toISOString().slice(0, 10);
  const base = Number(total) || 0;
  const per = Math.round((base / cnt) * 100) / 100;
  const rows = [];
  let running = 0;
  for (let i = 0; i < cnt; i++) {
    const isLast = i === cnt - 1;
    const amount = isLast ? Math.round((base - running) * 100) / 100 : per;
    running += per;
    rows.push({
      amount,
      date: i === 0 ? start : addMonthsISO(start, i),
      mode: mode || "UPI",
      note: `Instalment ${i + 1} of ${cnt}`,
    });
  }
  return rows;
}

export default function SalesLog() {
  // Read "Log Renewal" quick-action query params (from Dashboard) synchronously so the
  // dialog opens on first paint with the fields pre-filled.
  const preset = (() => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    const member_name = url.searchParams.get("member_name");
    if (!member_name) return null;
    const p = {
      member_name,
      customer_phone: url.searchParams.get("customer_phone") || "",
      programme: url.searchParams.get("programme") || "",
      status: url.searchParams.get("status") || "Renewal",
    };
    window.history.replaceState({}, "", url.pathname);
    return p;
  })();

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [period, setPeriod] = useState(() => (new URLSearchParams(window.location.search).get("q") ? "all" : "this_month"));  // Week/Month/…, or "custom"; all time when opened from Accounts with ?q=
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  // Type / Status / Programme / Pay mode / By dropdowns, remembered per browser.
  const [colFilters, setColFilters] = useState(loadColumnFilters);
  const [showColFilters, setShowColFilters] = useState(false);
  useEffect(() => { try { window.localStorage.setItem(COL_FILTERS_KEY, JSON.stringify(colFilters)); } catch { /* storage off */ } }, [colFilters]);
  const colFiltersOn = COLUMN_FILTERS.some((c) => colFilters[c.key] !== ALL);
  const [open, setOpen] = useState(!!preset);
  const [edit, setEdit] = useState(null);
  const [initialForNew, setInitialForNew] = useState(preset);
  // Bumped every time the New Sale dialog opens so SaleDialog remounts with a fresh state
  // and the previous entry's values do not linger.
  const [newSaleKey, setNewSaleKey] = useState(0);

  // One line per sale, plus a "Balance payment" line in the month each later payment came in.
  // Each line keeps its sale (`_sale`) for editing; its amounts are what came in on that line's date.
  // Deleting a sale (CR-0038): admins delete; everyone else sends a request an admin approves (21 Oct 2026).
  const { user } = useAuth();
  const isAdmin = atLeast(user?.role, "admin");
  const [delRequested, setDelRequested] = useState(() => new Set());
  const loadDelRequests = useCallback(() => listGymSaleDeletions()
    .then((list) => setDelRequested(new Set((list || []).filter((d) => d.status === "requested").map((d) => d.sale_id))))
    .catch(() => {}), []);
  useEffect(() => { loadDelRequests(); }, [loadDelRequests]);
  const refresh = useCallback(() => listSalesLedger().then((entries) => setRows(entries.map((e) => ({
    ...e.sale, ...e, id: e.entry_id, _sale: e.sale, member_name: e.sale.member_name,
    status: e.kind === "balance" ? "Balance payment" : e.kind === "refund" ? "Refund" : e.sale.status,
    pending_payment: e.kind === "sale" ? e.sale.pending_payment : 0,
    payment_mode: e.payment_mode || e.sale.payment_mode,
  })))), []);
  useEffect(() => { refresh(); }, [refresh]);
  // New Consult sends the rep here (?lead=<consult id>) to take the payment on the consult's sale.
  const [openLead, setOpenLead] = useState(() => new URLSearchParams(window.location.search).get("lead"));
  useEffect(() => {
    if (!openLead || !rows.length) return;
    const hit = rows.find((r) => r.kind === "sale" && r._sale.lead_id === openLead);
    if (hit) { setEdit(hit._sale); revealDate(hit._sale.date); }
    else toast.error("Couldn't find this consult's sale. Search by name or phone, or ask a manager.");
    setOpenLead(null);
    window.history.replaceState({}, "", window.location.pathname);
  }, [openLead, rows]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    const active = COLUMN_FILTERS.filter((c) => colFilters[c.key] !== ALL);
    return rows.filter((r) => {
      if (!rowInPeriod(r.date, period, customRange)) return false;
      for (const c of active) {
        const v = colVal(c, r);
        if (colFilters[c.key] === BLANK ? v !== "" : v !== colFilters[c.key]) return false;
      }
      if (!q) return true;
      return [r.member_name, r.customer_phone, r.programme, r.payment_mode, r.payment_received_by, r.remarks, r.status]
        .some((v) => (v || "").toString().toLowerCase().includes(s));
    });
  }, [rows, q, period, customRange, colFilters]);

  // Dropdown choices: the values in the loaded rows (any period), sorted, no blanks.
  const colOptions = useMemo(() => Object.fromEntries(COLUMN_FILTERS.map((c) => {
    const vals = new Set();
    let hasBlank = false;
    rows.forEach((r) => { const v = colVal(c, r); if (v) vals.add(v); else hasBlank = true; });
    return [c.key, { values: [...vals].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })), hasBlank: !!c.blank && hasBlank }];
  })), [rows]);

  // A back-dated sale would vanish under "This month": switch the list to its month.
  const revealDate = (date) => {
    if (!date || rowInPeriod(date, period, customRange)) return;
    setPeriod("custom");
    setCustomRange(monthRangeOf(date));
    toast(`Showing ${monthLabelOf(date)} — this sale is dated ${String(date).slice(8, 10)}-${String(date).slice(5, 7)}-${String(date).slice(0, 4)}`);
  };

  const totals = useMemo(() => {
    const paid = (filtered || []).reduce((a, r) => a + (r.paid || 0), 0);
    const net = (filtered || []).reduce((a, r) => a + (r.net_received || 0), 0);
    const pending = (filtered || []).reduce((a, r) => a + (r.pending_payment || 0), 0);
    const gst = (filtered || []).reduce((a, r) => a + (r.gst || 0), 0);
    const fee = (filtered || []).reduce((a, r) => a + (r.platform_fee_deducted || 0), 0);
    return { paid, net, pending, gst, fee };
  }, [filtered]);

  const remove = async (id) => {
    try {
      if (isAdmin) {
        if (!window.confirm("Delete this sale?")) return;
        await deleteSale(id); toast.success("Sale deleted"); refresh();
        return;
      }
      const reason = window.prompt("Deleting this sale needs an admin's approval. Why should it go? (e.g. entered twice)");
      if (!reason || !reason.trim()) return;
      await requestSaleDeletion(id, reason.trim());
      toast.success("Sent to an admin for approval");
      loadDelRequests();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  const DelButton = ({ sale, small }) => (delRequested.has(sale.id)
    ? <span className="text-[10px] text-amber-300 px-1" title="Waiting for an admin to approve or decline" data-testid={`delete-requested-${sale.id}`}>Delete requested</span>
    : <button type="button" onClick={() => remove(sale.id)} className={`${small ? "p-1" : "p-2"} text-zinc-500 hover:text-rose-400`}
        title={isAdmin ? "Delete this sale" : "Ask an admin to delete this sale"} aria-label={`${isAdmin ? "Delete" : "Request deletion of"} sale for ${sale.member_name}`}
        data-testid={`delete-sale-${sale.id}`}><Trash2 className={small ? "h-3.5 w-3.5" : "h-4 w-4"} /></button>);

  return (
    <div className="space-y-6" data-testid="sales-log-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">All Sales · every payment in one ledger</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">All Sales</h2>
          <p className="text-sm text-zinc-400 mt-1">Renewals, upsells and repeat payments. Front End sales appear here automatically once the lead is marked sold.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select data-testid="sales-log-select-132" value={period} onValueChange={setPeriod}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] h-10 w-full sm:w-40" data-testid="all-sales-period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {(PERIOD_OPTIONS || []).map((p) => (
                <SelectItem data-testid="sales-log-select-item-138" key={p.value} value={p.value} className="focus:bg-[#1a1a1a]">{p.label}</SelectItem>
              ))}
              <SelectItem value="custom" className="focus:bg-[#1a1a1a]" data-testid="all-sales-period-custom">Custom dates…</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="all-sales-custom-range">
              <div className="flex-1 sm:flex-none sm:w-36"><DatePicker value={customRange.from} onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))} placeholder="From" data-testid="all-sales-range-from" /></div>
              <span className="text-xs text-zinc-500">to</span>
              <div className="flex-1 sm:flex-none sm:w-36"><DatePicker value={customRange.to} onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))} placeholder="To" data-testid="all-sales-range-to" /></div>
            </div>
          )}
          <div className="relative w-full sm:w-auto order-first sm:order-none">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member, mode…" className="pl-9 bg-[#0a0a0a] border-[#262626] w-full sm:w-56" data-testid="sales-search" />
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setInitialForNew(null); if (v) setNewSaleKey((k) => k + 1); }}>
            <DialogTrigger data-testid="sales-log-dialog-trigger-147" asChild>
              <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5 max-sm:fixed max-sm:right-4 max-sm:z-30 max-sm:bottom-[calc(5rem+env(safe-area-inset-bottom))] max-sm:h-12 max-sm:shadow-lg max-sm:shadow-black/50" data-testid="new-sale-btn" data-write><Plus className="h-4 w-4 mr-1.5" /> New Sale</Button>
            </DialogTrigger>
            <SaleDialog key={newSaleKey} mode="create" initial={initialForNew || undefined} onDone={(saved) => { setOpen(false); setInitialForNew(null); refresh(); revealDate(saved?.date); }} />
          </Dialog>
          <Button type="button" variant="outline" className="rounded-full border-[#262626] bg-transparent hover:bg-[#161616]" data-testid="existing-member-6wc-btn" data-write
            onClick={() => { setInitialForNew({ status: EXISTING_MEMBER, fe_pt_pe: "FE", payment_received_by: EXISTING_MEMBER, category: "membership" }); setNewSaleKey((k) => k + 1); setOpen(true); }}>
            Existing member 6WC
          </Button>
        </div>
      </div>

      <div data-testid="all-sales-column-filters">
        <div className="flex items-center gap-3 md:hidden">
          <button type="button" onClick={() => setShowColFilters((v) => !v)} aria-expanded={showColFilters}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#262626] px-3 py-1.5 text-xs text-zinc-300" data-testid="all-sales-filters-toggle">
            Filters{colFiltersOn ? " · on" : ""}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showColFilters ? "rotate-180" : ""}`} />
          </button>
          {colFiltersOn && <button type="button" onClick={() => setColFilters({ ...NO_COLUMN_FILTERS })} className="text-xs text-blue-400 hover:underline">Clear filters</button>}
        </div>
        <div className={`flex flex-wrap items-center gap-2 max-md:mt-3 ${showColFilters ? "" : "max-md:hidden"}`}>
          {COLUMN_FILTERS.map((c) => {
            const { values, hasBlank } = colOptions[c.key];
            const cur = colFilters[c.key];
            const stale = cur !== ALL && cur !== BLANK && !values.includes(cur);
            return (
              <Select key={c.key} value={cur} onValueChange={(v) => setColFilters((f) => ({ ...f, [c.key]: v }))}>
                <SelectTrigger className={`bg-[#0a0a0a] border-[#262626] h-9 text-xs w-[calc(50%-0.25rem)] sm:w-40 ${cur !== ALL ? "border-blue-500/60 text-zinc-100" : "text-zinc-400"}`}
                  aria-label={`Filter by ${c.label}`} data-testid={`all-sales-filter-${c.key}`}>
                  <span className="truncate">{c.label}: {cur === ALL ? "All" : cur === BLANK ? "(blank)" : cur}</span>
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-h-72">
                  <SelectItem value={ALL} className="focus:bg-[#1a1a1a]">All</SelectItem>
                  {stale && <SelectItem value={cur} className="focus:bg-[#1a1a1a]">{cur}</SelectItem>}
                  {values.map((v) => <SelectItem key={v} value={v} className="focus:bg-[#1a1a1a]">{v}</SelectItem>)}
                  {hasBlank && <SelectItem value={BLANK} className="focus:bg-[#1a1a1a]">(blank)</SelectItem>}
                </SelectContent>
              </Select>
            );
          })}
          {colFiltersOn && <button type="button" onClick={() => setColFilters({ ...NO_COLUMN_FILTERS })} className="max-md:hidden text-xs text-blue-400 hover:underline px-1" data-testid="all-sales-clear-filters">Clear filters</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Paid" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totals.paid)}`} />
        <Kpi label="Processing fee" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totals.fee)}`} tint="rose" />
        <Kpi label="GST" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totals.gst)}`} />
        <Kpi label="Net received" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totals.net)}`} tint="emerald" />
        <Kpi label="Pending payment" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totals.pending)}`} tint="amber" />
      </div>

      {/* Phones: one card per sale */}
      <div className="md:hidden space-y-2 pb-16" data-testid="sale-cards">
        {filtered.map((r) => {
          const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
          return (
            <div key={r.id} className="card-surface p-3.5" data-testid={`sale-card-${r.id}`}>
              <button type="button" onClick={() => setEdit(r._sale)} className="w-full text-left" aria-label={`Open sale for ${r.member_name}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.member_name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {[new Date(r.date).toLocaleDateString(undefined, { day: "numeric", month: "short" }), r.fe_pt_pe, r.programme || r.item].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-emerald-300">{cur}{fmtINR(r.net_received)}</div>
                    <div className="text-[10px] text-zinc-500">net · paid {cur}{fmtINR(r.paid)}</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                  {r.status && <span className={r.kind === "balance" ? "text-sky-300" : r.kind === "refund" ? "text-rose-300" : ""}>{statusLabel(r.status, r._sale)}</span>}
                  {r.payment_mode && <span>{r.payment_mode}</span>}
                  {r.payment_received_by && <span>by {r.payment_received_by}</span>}
                  {(r.pending_payment || 0) > 0 && <span className="text-amber-300">pending {cur}{fmtINR(r.pending_payment)}</span>}
                  {r.kind === "sale" && <SignoffBadge sale={r._sale} />}
                </div>
              </button>
              {r.kind === "sale" && (
                <div className="mt-3 pt-2.5 border-t border-[#1f1f1f] flex items-center justify-end gap-1">
                  <BillButton kind="sale" id={r._sale.id} billed={r.bills_raised} onBilled={refresh} />
                  <button type="button" onClick={() => setEdit(r._sale)} className="p-2 text-zinc-400 hover:text-blue-400" aria-label={`Edit sale for ${r.member_name}`}><Pencil className="h-4 w-4" /></button>
                  {!r._sale.lead_id && <DelButton sale={r._sale} />}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="card-surface px-4 py-10 text-center text-sm text-zinc-500">{colFiltersOn ? "No sales match these filters." : "No sales in this period."}</div>}
      </div>

      <div className="card-surface overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Member</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Programme</th>
                <th className="px-4 py-3 font-medium">Pay mode</th>
                <th className="px-4 py-3 font-medium text-right">Paid</th>
                <th className="px-4 py-3 font-medium text-right" title="Tax included in the amount paid">GST</th>
                <th className="px-4 py-3 font-medium text-right" title="Payment gateway / credit card processing fee">Fee</th>
                <th className="px-4 py-3 font-medium text-right">Net</th>
                <th className="px-4 py-3 font-medium">By</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((r) => (
                <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]">
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(r.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-medium">{r.member_name}</td>
                  <td className="px-4 py-3 text-zinc-300 font-mono text-xs">
                    {r.fe_pt_pe || "—"}
                    {(r.fe_pt_pe || "").toLowerCase() === "other" && r.fe_pt_pe_other && (
                      <span className="ml-1 text-zinc-500 font-normal">→ {r.fe_pt_pe_other}</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-xs ${r.kind === "balance" ? "text-sky-300" : r.kind === "refund" ? "text-rose-300" : "text-zinc-400"}`}>
                    {statusLabel(r.status, r._sale) || "—"}
                    {r.kind === "sale" && <div><SignoffBadge sale={r._sale} /></div>}
                    {r.kind === "refund" && (
                      <div className="text-[10px] text-rose-300/80" data-testid={`refund-full-${r.id}`}
                        title="Refunds are the full amount the member paid, including GST. The GST part comes back on the tax return, so Net shows only the revenue reversed.">
                        {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(Math.abs(r.paid))} returned in full
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{r.programme || r.item || "—"}{r.item_description && <span className="text-zinc-500"> · {r.item_description}</span>}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{r.payment_mode || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.paid)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-300/80">{(r.gst || 0) !== 0 ? `${r.gst < 0 ? "-" : ""}${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(Math.abs(r.gst))}` : <span className="text-zinc-700">—</span>}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-300">{(r.platform_fee_deducted || 0) > 0 ? `-${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(r.platform_fee_deducted)}` : <span className="text-zinc-700">—</span>}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.net_received)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{r.payment_received_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {r.kind === "sale" && <BillButton kind="sale" id={r._sale.id} billed={r.bills_raised} onBilled={refresh} />}
                      <button onClick={() => setEdit(r._sale)} className="text-zinc-500 hover:text-blue-400 p-1" data-testid={`edit-sale-${r._sale.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      {r.kind === "sale" && !r._sale.lead_id && <DelButton sale={r._sale} small />}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={11} className="px-4 py-14 text-center text-sm text-zinc-500">{colFiltersOn ? "No sales match these filters." : "No sales logged yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        {edit && <SaleDialog mode="edit" initial={edit} onDone={(saved) => { setEdit(null); refresh(); revealDate(saved?.date); }} />}
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tint }) {
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 tabular-nums ${tint === "emerald" ? "text-emerald-400" : tint === "amber" ? "text-amber-400" : ""}`}>{value}</div>
    </div>
  );
}

function SaleDialog({ mode, initial, onDone }) {
  const { activeFacility, effectiveRole } = useAuth();
  const isManager = atLeast(effectiveRole, "manager");
  // A new sale: the member checks their name, phone and email before it's saved (their bill goes there).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmedRef = useRef("");
  // Facility taxation config — UAE gyms flip tax_enabled off so every calc treats tax as 0
  // and the tax input is disabled/greyed out below.
  const taxRate = Number(activeFacility?.tax_rate ?? 0.05);
  const taxEnabled = activeFacility?.tax_enabled !== false;
  const taxName = activeFacility?.tax_name || "GST";
  const taxPctText = `${(taxRate * 100).toFixed(taxRate * 100 % 1 === 0 ? 0 : 2)}%`;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [f, setF] = useState(() => ({
    // Blank slate apart from the sale date, which defaults to today (local) and can be
    // changed with the calendar for back-dated entries.
    member_name: "", email: "", date: todayLocal(activeFacility?.timezone),
    fe_pt_pe: "", fe_pt_pe_other: "", status: "", programme: "",
    category: "membership", item: "", item_description: "", variant: "", quantity: 1,
    payment_mode: "", paid: 0, platform_fee_deducted: 0, gst: 0, net_received: 0,
    payment_received_by: "", pending_payment: 0, remarks: "", bills_raised: false,
    blood_test: false, customer_phone: "",
    start_date: "", end_date: "",
    referred_by_customer_id: "", referred_by_name: "",
    lead_nurture_exec: "", accountability_coach: "", member_manager: "",
    payment_type: "full", split_payments: [],
    ...(initial || {}),
  }));
  const [busy, setBusy] = useState(false);
  // Once a sale has its own payments, "Paid" is their total and is edited through them.
  const [hasPayments, setHasPayments] = useState(false);
  useEffect(() => {
    if (mode === "edit" && initial?.id && !initial?.lead_id) listSalePayments(initial.id).then((r) => setHasPayments(r.length > 0)).catch(() => {});
  }, [mode, initial?.id, initial?.lead_id]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [typeOpts, setTypeOpts] = useState(DEFAULT_TYPE_OPTS);
  const [savingType, setSavingType] = useState(false);
  const [instalmentCount, setInstalmentCount] = useState(() => {
    const list = initial?.split_payments || [];
    return list.length >= 1 && list.length <= 3 ? list.length : 3;
  });
  const [priceOverride, setPriceOverride] = useState(false); // did the user manually change amount away from catalog price?
  useEffect(() => { listCustomers().then(setCustomers).catch(() => {}); }, []);
  useEffect(() => { getProgrammeCatalog().then((r) => setCatalog(r.items || [])).catch(() => {}); }, []);
  const payModes = usePaymentModes(f.payment_mode, ...(f.split_payments || []).map((sp) => sp.mode));
  const [revenue, setRevenue] = useState({ categories: {}, items: {} });
  useEffect(() => { getRevenueItems().then(setRevenue).catch(() => {}); }, []);
  // Merch stock: if the picked item has stock lines at this gym, the sale needs a size and takes stock.
  const [merchStock, setMerchStock] = useState([]);
  useEffect(() => { getMerchStock().then((r) => setMerchStock(r.stock || [])).catch(() => {}); }, []);
  const stockLines = f.category === "merch" ? merchStock.filter((r) => merchKey(r.item) === merchKey(f.item)) : [];
  const sized = stockLines.some((r) => r.variant);
  // When editing, this sale's own units are back on the shelf for the purpose of choosing again.
  const available = (r) => r.on_hand + (initial?.stock_deducted?.stock_id === r.id ? initial.stock_deducted.qty : 0);
  const category = f.category || "membership";
  const isMembership = category === "membership";
  useEffect(() => {
    getSaleTypes().then((r) => {
      const active = (r.items || []).filter((x) => x.active !== false).map((x) => x.name);
      if (active.length) setTypeOpts(active);
    }).catch(() => {});
  }, []);
  // Tax-disabled facilities (e.g. UAE): coerce gst → 0 and recompute net_received so
  // an edited legacy sale stops carrying stale tax figures the moment the toggle flips.
  useEffect(() => {
    if (taxEnabled) return;
    setF((s) => {
      if ((Number(s.gst) || 0) === 0) return s;
      const paid = Number(s.paid) || 0;
      const fee = Number(s.platform_fee_deducted) || 0;
      return {
        ...s,
        gst: 0,
        net_received: Math.max(0, Math.round((paid - fee) * 100) / 100),
      };
    });
  }, [taxEnabled]);

  // NOTE (Feb 2026): the previous "auto-populate paid from catalog price on catalog load"
  // effect has been removed — the user explicitly asked for no autofills on New Sale.
  // Programme change still updates `paid` when the user picks a programme (in `set()`),
  // which is a user action, not a silent autofill.

  // Catalog prices are ex-GST; members pay price + GST, and staff enter what was collected.
  const priceOf = (prog) => {
    if (!prog) return null;
    const row = (catalog || []).find((x) => x.name && x.name.toLowerCase() === String(prog).toLowerCase());
    if (!row) return null;
    const base = Number(row.price || 0);
    return taxEnabled ? Math.round(base * (1 + taxRate) * 100) / 100 : base;
  };
  // Programme dropdown: every active catalog programme, the same list as New Consult (29 Oct 2026).
  // Falls back to ALL_SALES_PROGRAMMES until the catalog fetch resolves.
  const allSalesPrograms = (() => {
    const list = (catalog || [])
      .filter((x) => x.active !== false)
      .map((x) => x.name)
      .filter(Boolean);
    return list.length ? list : PROG_OPTS;
  })();

  const isAnnual = (f.programme || "").toLowerCase() === "annual";
  const isSplit = isAnnual && (f.payment_type || "full") === "split";

  // Platform fee follows Settings → Payment gateway fees (percent + flat, per mode), the same
  // rule the server uses, recalculated when the amount or mode changes (CR-0015). Once
  // someone types a fee by hand it's left alone.
  const [feeTable, setFeeTable] = useState([]);
  useEffect(() => { getPaymentFees().then((r) => setFeeTable(r.items || [])).catch(() => {}); }, []);
  const feeManual = useRef(false);
  // Annual balance (CR-0016): catalog price + GST − paid, filled in as Paid is entered, until
  // someone types the balance by hand. Arithmetic like GST/net, not a guessed price.
  const pendingManual = useRef(mode !== "create");
  // The 6WC finisher price, when this member has finished a 6 Week Challenge (FPA, YoActiv or Accountability)
  const [finisher, setFinisher] = useState(null);     // {programme, price, finisher_price, finisher}
  const finisherRef = useRef(null);
  useEffect(() => {
    const prog = (f.programme || "").trim();
    if (!prog || (f.customer_phone || "").replace(/\D/g, "").length < 9) { setFinisher(null); finisherRef.current = null; return undefined; }
    const t = setTimeout(() => getProgrammePrice(prog, f.customer_phone, (f.date || "").slice(0, 10))
      .then((r) => { const v = { ...r, programme: prog }; setFinisher(v); finisherRef.current = v; })
      .catch(() => { setFinisher(null); finisherRef.current = null; }), 400);
    return () => clearTimeout(t);
  }, [f.programme, f.customer_phone, f.date]);
  const finisherPriceFor = (prog) => {
    const r = finisherRef.current;
    return r && r.finisher && r.finisher_price > 0 && (r.programme || "").toLowerCase() === String(prog || "").toLowerCase() ? r.finisher_price : null;
  };
  const feeFor = (mode, amount) => {
    const row = feeTable.find((x) => x.active !== false && (x.mode || "").trim().toLowerCase() === (mode || "").trim().toLowerCase());
    const amt = Number(amount) || 0;
    if (!row || amt <= 0) return 0;
    return Math.round((amt * (Number(row.percent) || 0) / 100 + (Number(row.flat) || 0)) * 100) / 100;
  };
  const autoFee = (s) => ((s.payment_type || "full") === "split"
    ? (s.split_payments || []).reduce((a, sp) => a + feeFor(sp.mode || s.payment_mode, sp.amount), 0)
    : feeFor(s.payment_mode, s.paid));
  // Re-derive fee (unless typed by hand), tax and net after the payments change.
  const withMoney = (s) => {
    const paid = Number(s.paid) || 0;
    const fee = feeManual.current ? Number(s.platform_fee_deducted) || 0 : Math.round(autoFee(s) * 100) / 100;
    const gst = taxEnabled ? taxIncluded(paid, taxRate) : 0;
    return { ...s, platform_fee_deducted: fee, gst, net_received: Math.max(0, Math.round((paid - fee - gst) * 100) / 100) };
  };
  const feeRow = feeTable.find((x) => x.active !== false && (x.mode || "").trim().toLowerCase() === (f.payment_mode || "").trim().toLowerCase());

  const set = (k, v) => {
    if (k === "platform_fee_deducted") feeManual.current = true;
    if (k === "pending_payment") pendingManual.current = true;
    setF((s) => {
      const next = { ...s, [k]: v };

      // NOTE: programme change no longer auto-populates `paid` — user asked for zero
      // autofills on New Sale. The catalog price is still shown as a helper hint next
      // to the Paid field so the user can copy it in manually if they want.
      if (k === "programme") {
        setPriceOverride(false);
        // Switching away from Annual resets any split state.
        if ((v || "").toLowerCase() !== "annual" && next.payment_type === "split") {
          next.payment_type = "full";
          next.split_payments = [];
        }
      }

      // Manual override tracking on paid field
      if (k === "paid") {
        const catalogPrice = priceOf(next.programme);
        if (catalogPrice !== null && catalogPrice > 0 && Number(v) !== catalogPrice && Number(v) !== finisherPriceFor(next.programme)) {
          setPriceOverride(true);
        }
      }

      // Instalments layout for annual+split — recompute rows from total paid + count
      // whenever the user hasn't manually edited individual amounts (we regenerate on programme/paid/count changes)
      let paid = Number(next.paid) || 0;
      if ((next.payment_type || "full") === "split") {
        paid = (next.split_payments || []).reduce((s, sp) => s + (Number(sp.amount) || 0), 0);
        next.paid = Math.round(paid * 100) / 100;
      }
      // GST / net_received are DERIVATIONS from user-entered numbers — keep them auto since
      // they're arithmetic, not defaults.
      if (!feeManual.current && ["paid", "payment_mode", "payment_type", "split_payments"].includes(k)) {
        next.platform_fee_deducted = Math.round(autoFee(next) * 100) / 100;
      }
      if (["paid", "platform_fee_deducted", "payment_mode", "payment_type", "split_payments"].includes(k)) {
        next.gst = taxEnabled ? taxIncluded(paid, taxRate) : 0;
        const fee = Number(next.platform_fee_deducted) || 0;
        next.net_received = Math.max(0, Math.round((paid - fee - next.gst) * 100) / 100);
      }
      if (!pendingManual.current && ["programme", "paid", "payment_type"].includes(k)) {
        const price = finisherPriceFor(next.programme) || priceOf(next.programme);   // a finisher's Annual is the finisher price
        const annualFull = (next.programme || "").toLowerCase() === "annual" && (next.payment_type || "full") !== "split";
        if (annualFull && price > 0 && Number(next.paid) > 0) {
          next.pending_payment = Math.max(0, Math.round((price - Number(next.paid)) * 100) / 100);
        }
      }
      // end_date auto-derivation removed — user asked for zero autofill on New Sale.
      return next;
    });
  };

  // Regenerate instalments whenever count/total/programme changes while in split mode.
  useEffect(() => {
    if (!isSplit) return;
    const total = Number(f.paid) || 0;
    if (total <= 0) return;
    setF((s) => withMoney({
      ...s,
      split_payments: buildEqualInstalments(total, instalmentCount, s.date || new Date().toISOString().slice(0, 10), s.payment_mode || "UPI"),
    }));
  }, [instalmentCount, isSplit]);

  // When user switches to split mode, initialise instalments
  const toggleSplit = (splitOn) => {
    if (splitOn) {
      setF((s) => withMoney({
        ...s,
        payment_type: "split",
        split_payments: buildEqualInstalments(Number(s.paid) || priceOf(s.programme) || 0, instalmentCount, s.date || new Date().toISOString().slice(0, 10), s.payment_mode || "UPI"),
      }));
    } else {
      setF((s) => withMoney({ ...s, payment_type: "full", split_payments: [] }));
    }
  };

  const editInstalment = (idx, k, v) => {
    setF((s) => {
      const next = [...(s.split_payments || [])];
      next[idx] = { ...next[idx], [k]: k === "amount" ? Number(v) || 0 : v };
      const sum = next.reduce((a, sp) => a + (Number(sp.amount) || 0), 0);
      const gst = taxEnabled ? taxIncluded(sum, taxRate) : 0;
      const fee = feeManual.current ? Number(s.platform_fee_deducted) || 0
        : Math.round(autoFee({ ...s, split_payments: next }) * 100) / 100;
      return {
        ...s,
        split_payments: next,
        paid: Math.round(sum * 100) / 100,
        platform_fee_deducted: fee,
        gst,
        net_received: Math.max(0, Math.round((sum - fee - gst) * 100) / 100),
      };
    });
  };

  const isFEConversion = (f.status || "").trim() === "FE Conversion";
  // A consult's sale (CR-0041): its money is taken under "Payments received" only. Price,
  // programme and the member's email live on the consult, so they aren't asked for here.
  const isConsultSale = mode === "edit" && !!initial?.lead_id;
  // Reward points off a renewal (same rules as renewing in the member app: min 50, up to 10% before tax).
  const [pts, setPts] = useState(null);
  const pointsEligible = mode === "create" && (f.category || "membership") === "membership" && !isSplit
    && ["Renewal", "Exit Return"].includes((f.status || "").trim()) && (f.customer_phone || "").replace(/\D/g, "").length >= 9;
  useEffect(() => {
    if (!pointsEligible) { setPts(null); return undefined; }
    const t = setTimeout(() => getSalePoints(f.customer_phone).then(setPts).catch(() => setPts(null)), 400);
    return () => clearTimeout(t);
  }, [pointsEligible, f.customer_phone]);
  const isExisting = (f.status || "").trim() === EXISTING_MEMBER;
  // Existing member's 6WC: the price (15% off) and the membership it pauses.
  const [pausePreview, setPausePreview] = useState(null);
  useEffect(() => {
    if (!isExisting) return;
    const t = setTimeout(() => {
      api.get("/sales/existing-member-6wc", { params: { phone: f.customer_phone || undefined, name: f.member_name || undefined, start: (f.start_date || f.date || "").slice(0, 10) || undefined } })
        .then((r) => {
          setPausePreview(r.data);
          // Programme only: New Sale has no price autofill (the price is shown in the box below).
          if (mode === "create") setF((s) => ({ ...s, programme: r.data.programme || s.programme }));
        }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [isExisting, mode, f.customer_phone, f.member_name, f.start_date, f.date]);

  // FE Retention rule: only the plan straight after a member's first 6 Week Challenge. A new sale for
  // someone who just finished their first 6WC is picked as FE Retention by default.
  const [retention, setRetention] = useState(null);
  const statusTouched = useRef(mode === "edit" || !!initial?.status && initial.status !== "Renewal");
  const autoPicked = useRef(null);   // the status it replaced, while FE Retention is our pick
  const undoAutoPick = () => {
    if (autoPicked.current === null) return;
    const back = autoPicked.current;
    autoPicked.current = null;
    setF((s) => ((s.status || "").trim() === "FE Conversion" ? { ...s, status: back } : s));
  };
  useEffect(() => {
    // Not a membership any more (e.g. switched to Event after picking the member): drop our pick (CR-0078).
    if (isExisting || isConsultSale || (f.category || "membership") !== "membership") { setRetention(null); undoAutoPick(); return undefined; }
    if (!(f.customer_phone || "").trim() && !(f.member_name || "").trim()) { setRetention(null); undoAutoPick(); return undefined; }
    const t = setTimeout(() => {
      api.get("/sales/fe-retention-check", { params: { phone: f.customer_phone || undefined, name: f.member_name || undefined,
        on: (f.date || "").slice(0, 10) || undefined, programme: f.programme || undefined, sale_id: initial?.id || undefined } })
        .then((r) => {
          setRetention(r.data);
          if (statusTouched.current) return;
          if (r.data.eligible) {
            setF((s) => {
              if (autoPicked.current === null) autoPicked.current = s.status || "Renewal";
              return { ...s, status: "FE Conversion" };
            });
          } else undoAutoPick();
        }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [isExisting, isConsultSale, f.category, f.customer_phone, f.member_name, f.date, f.programme, initial?.id]);
  const retentionBlocked = isFEConversion && retention && !retention.eligible && (f.date || "").slice(0, 10) >= retention.rule_from;

  // Auto-fill Accountability Coach from the original FE lead when status = FE Conversion
  useEffect(() => {
    if (!isFEConversion) return;
    if ((f.accountability_coach || "").trim()) return;
    const phone = (f.customer_phone || "").trim();
    const name = (f.member_name || "").trim();
    if (!phone && !name) return;
    const params = phone ? { phone } : { name };
    api.get("/leads/lookup-coach", { params })
      .then((r) => {
        const coach = r.data?.acc_coach;
        if (coach) setF((s) => (s.accountability_coach ? s : { ...s, accountability_coach: coach }));
      })
      .catch(() => {});
  }, [isFEConversion, f.customer_phone, f.member_name]);

  const submit = async (e) => {
    e.preventDefault();
    if (nameProblem(f.member_name)) return toast.error(`Member name: ${nameProblem(f.member_name)}`);
    if (!isConsultSale && !(f.email || "").trim()) return toast.error("Email is required");
    if (!isConsultSale && isMembership && !(f.member_manager || "").trim()) return toast.error("Member Manager is required for every membership sale.");
    if (isFEConversion && !(f.accountability_coach || "").trim()) {
      return toast.error(`Accountability Coach is required for ${feRetentionLabel()} sales: this coach earns the incentive.`);
    }
    if (isSplit) {
      if (!f.split_payments || f.split_payments.length < 1 || f.split_payments.length > 3) {
        return toast.error("Annual split payments need between 1 and 3 instalments.");
      }
    }
    if (!isConsultSale && !isSplit && Number(f.paid) > 0 && !(f.payment_mode || "").trim()) return toast.error("Pick the payment mode (how they paid).");
    if (ptsError) return toast.error(`Reward points: ${ptsError}`);
    if (isExisting && !(f.customer_phone || "").trim()) return toast.error("Add the member's phone, so FPA can find and pause their membership.");
    if (retentionBlocked && !(retention.can_override && (f.fe_retention_override_reason || "").trim())) {
      return toast.error(`Not ${feRetentionLabel()}: ${retention.reason}`);
    }
    const detailsKey = [f.member_name, f.customer_phone, f.email].map((v) => (v || "").trim()).join("|");
    if (mode === "create" && confirmedRef.current !== detailsKey) { setConfirmOpen(true); return undefined; }
    setBusy(true);
    try {
      if (mode === "create") await createSale({ ...f, points_used: pointsEligible && Number(f.points_used) > 0 ? Number(f.points_used) : null });
      else if (initial?.lead_id) {
        // A consult's sale: its money comes from the payments below, so the form never overwrites it.
        const body = { ...f };
        CONSULT_MONEY_FIELDS.forEach((k) => delete body[k]);
        await updateSale(initial.id, body);
      } else await updateSale(initial.id, f);
      toast.success("Saved"); onDone({ date: (f.date || "").slice(0, 10) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const catalogPrice = priceOf(f.programme);
  const finisherPrice = finisher && finisher.finisher && finisher.finisher_price > 0
    && (finisher.programme || "").toLowerCase() === (f.programme || "").toLowerCase() ? finisher.finisher_price : null;
  const basePrice = finisherPrice || catalogPrice;     // what this member should pay before any points
  const usedPts = pointsEligible && pts ? Math.max(0, Math.floor(Number(f.points_used) || 0)) : 0;
  const ptsOff = usedPts * (pts?.value_per_point || 0);                       // before tax
  const ptsOffInclTax = Math.round(ptsOff * (1 + (pts?.tax_rate || 0)) * 100) / 100;
  const priceAfterPts = basePrice !== null && basePrice > 0 ? Math.round((basePrice - ptsOffInclTax) * 100) / 100 : null;
  const overrideFlag = catalogPrice !== null && catalogPrice > 0 && Number(f.paid) > 0
    && (usedPts ? Number(f.paid) !== priceAfterPts : ![catalogPrice, finisherPrice].includes(Number(f.paid)));
  const maxPts = (() => {
    if (!pts || !(pts.value_per_point > 0)) return 0;
    const base = basePrice > 0 ? basePrice / (1 + (pts.tax_rate || 0)) : (Number(f.paid) || 0) / (1 + (pts.tax_rate || 0));
    const cap = Math.floor(Math.round((base * pts.max_discount_pct / 100 / pts.value_per_point) * 1e6) / 1e6);
    const most = Math.max(0, Math.min(pts.balance, cap));
    return most >= pts.min_redeem_points ? most : 0;
  })();
  const ptsError = usedPts > 0 && (usedPts < (pts?.min_redeem_points || 0) ? `Use at least ${pts.min_redeem_points} points`
    : usedPts > (pts?.balance || 0) ? `They have ${pts.balance} points` : usedPts > maxPts ? `Up to ${maxPts} points (${pts.max_discount_pct}% of the price before ${taxName})` : null);

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sale-dialog">
      <DialogHeader>
        <div className="flex items-center justify-between gap-2 pr-8">
          <DialogTitle className="font-display text-xl">{mode === "create" ? "New Sale" : `Edit — ${initial.member_name}`}</DialogTitle>
          {mode === "edit" && (
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-500/60 rounded-full px-2.5 py-1 hover:bg-amber-500/10 transition-colors"
              data-testid="sale-history-btn"
              title="See every edit to this sale"
            >
              <History className="h-3 w-3" /> History
            </button>
          )}
        </div>
      </DialogHeader>
      {mode === "edit" && (
        <AuditHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="sale"
          entityId={initial.id}
          entityLabel={initial.member_name}
        />
      )}
      <ConfirmDetails open={confirmOpen}
        details={{ name: f.member_name, phone: f.customer_phone, email: f.email,
          extra: [["For", f.programme || f.item], ["Paid now", Number(f.paid) ? `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(f.paid)}` : ""]] }}
        onEdit={() => setConfirmOpen(false)} onUseSuggestion={(v) => set("email", v)}
        onConfirm={() => { confirmedRef.current = [f.member_name, f.customer_phone, f.email].map((v) => (v || "").trim()).join("|"); setConfirmOpen(false); submit({ preventDefault() {} }); }} />
      <form data-testid="sales-log-form-468" onSubmit={submit} className="space-y-4 mt-2">
        {isConsultSale && (
          <div className="space-y-2" data-testid="consult-sale-payments">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 text-xs text-emerald-100">
              Take the payment here: enter the amount, date and mode, then press <span className="font-semibold">Add payment</span>. It&apos;s saved straight away and the bill is emailed to the member.
            </div>
            <PaymentsLedger leadId={initial.lead_id}
              onChange={(list) => { const paid = (list || []).reduce((a, p) => a + (Number(p.amount) || 0), 0); setF((s) => ({ ...s, paid })); }} />
            <div className="text-[11px] text-zinc-500">The programme, price and refunds stay on the consult (New Consult).</div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Member name" required>
            <CustomerAutocomplete
              customers={customers}
              value={f.member_name}
              phone={f.customer_phone}
              onPickCustomer={(c) => {
                set("member_name", c.name || "");
                set("customer_phone", c.phone || "");
                set("email", c.email || f.email || "");
              }}
              onChangeText={(v) => set("member_name", v)}
              testid="sale-name-input"
            />
            {f.member_name && nameProblem(f.member_name) && (
              <p className="text-[11px] text-rose-400 mt-1">
                {nameProblem(f.member_name)}
                {autoFixName(f.member_name) !== f.member_name && !nameProblem(autoFixName(f.member_name)) && (
                  <button type="button" className="ml-2 underline text-rose-300" onClick={() => set("member_name", autoFixName(f.member_name))}>Fix it</button>
                )}
              </p>
            )}
          </F>
          <F label="Email" required><Input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" data-testid="sale-email-input" placeholder="Required" />
            <EmailTypoHint email={f.email} name={f.member_name} onFix={(v) => set("email", v)} testid="sale-email-warning" /></F>
          <F label="Sale date" required><DatePicker value={(f.date || "").slice(0, 10)} onChange={(e) => set("date", e.target.value)} data-testid="sale-date-input" /></F>
          <F label="Phone (for renewal tracking)"><Input data-testid="sales-log-input-486" value={f.customer_phone || ""} onChange={(e) => set("customer_phone", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" /></F>
          {isExisting ? (
            <>
              <F label="Sales rep"><Input readOnly value={EXISTING_MEMBER} className="bg-[#0a0a0a] border-[#262626] text-zinc-400" title="No incentive on an existing member's challenge" data-testid="sale-sales-rep-locked" /></F>
              <F label="Type"><Input readOnly value="FE" className="bg-[#0a0a0a] border-[#262626] text-zinc-400" /></F>
              <F label="Status"><Input readOnly value={EXISTING_MEMBER} className="bg-[#0a0a0a] border-[#262626] text-zinc-400" /></F>
            </>
          ) : (
            <>
              <F label="Payment received by (Sales Ex)"><RosterSelect roleType="sales_rep" value={f.payment_received_by || ""} onChange={(v) => set("payment_received_by", v)} testid="sale-sales-rep-input" /></F>
              <F label="Type (renewal / upsell)"><SelectBox data-testid="sales-log-select-box-488" value={f.fe_pt_pe} onChange={(v) => set("fe_pt_pe", v)} opts={typeOpts} testid="sale-type-select" /></F>
              <F label="Status"><SelectBox data-testid="sales-log-select-box-489" value={f.status} onChange={(v) => { statusTouched.current = true; autoPicked.current = null; set("status", v); }} opts={STATUS_OPTS} labelOf={(o) => statusLabel(o, initial || {})} /></F>
              {retention && (isFEConversion || retention.eligible) && (
                <div className={`md:col-span-2 rounded-md border px-3 py-2 text-xs ${retention.eligible ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-amber-500/30 bg-amber-500/5 text-amber-300"}`} data-testid="fe-retention-check">
                  {retention.eligible
                    ? <>{feRetentionLabel()}: {retention.reason}{!isFEConversion && " Pick it as the status."}</>
                    : <>Not {feRetentionLabel()}: {retention.reason}</>}
                  {retentionBlocked && retention.can_override && (
                    <div className="mt-2">
                      <Input placeholder="Admin: reason to count it anyway" value={f.fe_retention_override_reason || ""}
                        onChange={(e) => set("fe_retention_override_reason", e.target.value)}
                        className="bg-[#0a0a0a] border-[#262626] text-zinc-100" data-testid="fe-retention-override-reason" />
                    </div>
                  )}
                  {initial?.fe_retention_override && (
                    <div className="mt-1 text-zinc-400">Counted anyway by {initial.fe_retention_override.by}: {initial.fe_retention_override.reason}</div>
                  )}
                </div>
              )}
            </>
          )}
          <F label="Category">
            <Select value={category} onValueChange={(v) => setF((s) => ({ ...s, category: v, item: "", programme: v === "membership" ? s.programme : "" }))}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="sale-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="membership">Membership</SelectItem>
                {Object.entries(revenue.categories || {}).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          {isExisting || isConsultSale ? (
            <F label="Programme"><Input readOnly value={f.programme || ""} className="bg-[#0a0a0a] border-[#262626] text-zinc-400" data-testid="sale-programme-locked" /></F>
          ) : isMembership ? (
            <F label="Programme"><SelectBox data-testid="sales-log-select-box-490" value={f.programme} onChange={(v) => set("programme", v)} opts={allSalesPrograms} /></F>
          ) : (
            <>
              <F label="What was sold" required><SelectBox value={f.item} onChange={(v) => setF((s) => ({ ...s, item: v, variant: "" }))} opts={(revenue.items || {})[category] || []} testid="sale-item-select" /></F>
              {(revenue.descriptions || {})[f.item] && (
                <div className="md:col-span-2 rounded-md border border-[#262626] bg-[#0a0a0a] px-3 py-2 text-xs text-zinc-400" data-testid="sale-item-description">
                  {revenue.descriptions[f.item]}
                </div>
              )}
              {stockLines.length > 0 && (
                <>
                  {sized && (
                    <F label="Size" required>
                      <Select value={f.variant || ""} onValueChange={(v) => set("variant", v)}>
                        <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="sale-variant-select"><SelectValue placeholder="Pick size" /></SelectTrigger>
                        <SelectContent>
                          {stockLines.map((r) => (
                            <SelectItem key={r.id} value={r.variant} disabled={available(r) <= 0}>
                              {r.variant} · {available(r) > 0 ? `${available(r)} in stock` : "out of stock"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </F>
                  )}
                  <F label="Quantity" required>
                    <Input type="number" min={1} value={f.quantity ?? 1} onChange={(e) => set("quantity", Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="bg-[#0a0a0a] border-[#262626]" data-testid="sale-quantity-input" />
                    {!sized && <div className="text-xs text-zinc-500 mt-1">{available(stockLines[0])} in stock</div>}
                  </F>
                </>
              )}
              <F label="Description"><Input value={f.item_description || ""} onChange={(e) => set("item_description", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" placeholder="Optional, e.g. Summer Shred batch 2" data-testid="sale-item-description" /></F>
            </>
          )}
          {!isConsultSale && <F label="Payment mode" required={Number(f.paid) > 0}><SelectBox data-testid="sales-log-select-box-491" value={f.payment_mode} onChange={(v) => set("payment_mode", v)} opts={payModes} /></F>}
        </div>
        {(f.fe_pt_pe || "").toLowerCase() === "other" && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.02] p-3" data-testid="fe-pt-pe-other-row">
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <F label="Specify what 'Other' is">
                <Input
                  value={f.fe_pt_pe_other || ""}
                  onChange={(e) => set("fe_pt_pe_other", e.target.value)}
                  placeholder="e.g. Merchandise, Nutrition pack, Assessment fee..."
                  className="bg-[#0a0a0a] border-[#262626]"
                  data-testid="fe-pt-pe-other-input"
                />
              </F>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={savingType || !(f.fe_pt_pe_other || "").trim()}
                onClick={async () => {
                  const name = (f.fe_pt_pe_other || "").trim();
                  if (!name) return;
                  setSavingType(true);
                  try {
                    const r = await quickAddSaleType(name);
                    const active = (r.items || []).filter((x) => x.active !== false).map((x) => x.name);
                    if (active.length) setTypeOpts(active);
                    if (r.added) {
                      toast.success(`'${name}' saved to Settings — now available in the Type dropdown`);
                    } else {
                      toast(`'${name}' already exists in Settings`);
                    }
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Save failed");
                  } finally { setSavingType(false); }
                }}
                className="border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10 h-9 whitespace-nowrap"
                data-testid="save-sale-type-btn"
              >
                <Bookmark className="h-3.5 w-3.5 mr-1" /> Save to Settings
              </Button>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1.5">Frequently used specifiers can be saved into the Sale Types catalog so they appear directly in the Type dropdown next time.</div>
          </div>
        )}
        {!isConsultSale && pointsEligible && pts && (
          <div className="rounded-md border border-[#262626] bg-[#0a0a0a] p-3 space-y-2" data-testid="sale-points">
            <div className="text-xs text-zinc-400">
              Reward points: <span className="text-zinc-100 font-mono">{fmtINR(pts.balance)}</span>
              {pts.balance > 0 && <> (worth {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(pts.balance * pts.value_per_point)})</>}
              {maxPts === 0 && <span className="text-zinc-500"> · {pts.balance < pts.min_redeem_points ? `needs ${pts.min_redeem_points} to use` : "pick the programme to see how many can be used"}</span>}
            </div>
            {maxPts > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Input type="number" min={0} value={f.points_used || ""} placeholder="Points to use" onChange={(e) => set("points_used", e.target.value)}
                  className="bg-[#0f0f0f] border-[#262626] font-mono w-36 h-9" data-testid="sale-points-input" />
                <Button type="button" variant="outline" className="h-9 border-[#2a2a2a] bg-transparent text-zinc-200" onClick={() => set("points_used", maxPts)}
                  data-testid="sale-points-max">Use max ({fmtINR(maxPts)})</Button>
                {usedPts > 0 && !ptsError && (
                  <span className="text-xs text-emerald-300">
                    {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(ptsOff)} off before {taxName}
                    {priceAfterPts !== null && <> · charge {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(priceAfterPts)}</>}
                  </span>
                )}
                {usedPts > 0 && !ptsError && priceAfterPts !== null && Number(f.paid) !== priceAfterPts && !hasPayments && (
                  <Button type="button" variant="ghost" className="h-9 text-blue-400" onClick={() => set("paid", priceAfterPts)} data-testid="sale-points-setpaid">
                    Set Paid to {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(priceAfterPts)}
                  </Button>
                )}
                {ptsError && <span className="text-xs text-rose-300">{ptsError}</span>}
              </div>
            )}
            <div className="text-[10px] text-zinc-500">Points come off their balance when you save. Paid is what they pay after points.</div>
          </div>
        )}
        {mode === "edit" && initial?.points_used > 0 && (
          <div className="text-xs text-zinc-400">{initial.points_used} reward points used ({window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(initial.points_discount)} off before {taxName}). Deleting the sale gives them back.</div>
        )}
        {!isConsultSale && (<>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <F label={`Paid${catalogPrice !== null && catalogPrice > 0 ? ` · catalog ${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(catalogPrice)}${finisherPrice ? ` · finisher ${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(finisherPrice)}` : ""}${taxEnabled ? ` incl. ${taxName}` : ""}` : ""}`}>
            <Input
              type="number"
              value={f.paid}
              onChange={(e) => set("paid", Number(e.target.value) || 0)}
              readOnly={isSplit || hasPayments}
              title={hasPayments ? "Comes from the payments below" : undefined}
              className={`bg-[#0a0a0a] border-[#262626] font-mono ${isSplit ? "text-emerald-300" : ""}`}
              data-testid="sale-paid-input"
            />
            {finisherPrice && !usedPts && Number(f.paid) !== finisherPrice && !isSplit && !hasPayments && (
              <button type="button" className="text-[10px] text-emerald-300 hover:underline mt-1 block text-left" data-testid="sale-use-finisher"
                onClick={() => set("paid", finisherPrice)}>
                Finished a 6 Week Challenge: use the finisher price {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(finisherPrice)}
              </button>
            )}
            {finisherPrice && Number(f.paid) === finisherPrice && (
              <div className="text-[10px] text-emerald-300/80 mt-1" data-testid="sale-finisher-applied">6WC finisher price</div>
            )}
            {overrideFlag && (
              <div className="text-[10px] text-amber-400/80 mt-1" data-testid="price-override-hint">
                Manual override · will be logged
              </div>
            )}
          </F>
          <F label="Platform fee">
            <Input data-testid="sales-log-input-552" type="number" value={f.platform_fee_deducted} onChange={(e) => set("platform_fee_deducted", Number(e.target.value) || 0)} className="bg-[#0a0a0a] border-[#262626] font-mono" />
            <div className="text-[10px] text-zinc-500 mt-1" data-testid="sale-fee-hint">
              {feeManual.current ? (
                <>Typed by hand · <button type="button" className="text-blue-400 hover:underline" onClick={() => { feeManual.current = false; setF((s) => withMoney(s)); }}>Auto</button></>
              ) : feeRow ? `Auto: ${feeRow.mode} ${Number(feeRow.percent) || 0}%${Number(feeRow.flat) ? ` + ${feeRow.flat}` : ""}` : "Auto from payment mode"}
            </div>
          </F>
          <F label={taxEnabled ? `${taxName} (${taxPctText} included, auto)` : `${taxName} · disabled for this gym`}>
            <Input
              readOnly
              disabled={!taxEnabled}
              value={taxEnabled ? f.gst : 0}
              className={`bg-[#0a0a0a] border-[#262626] font-mono ${taxEnabled ? "text-zinc-400" : "text-zinc-600 opacity-50 cursor-not-allowed"}`}
              data-testid="sale-gst-field"
            />
          </F>
          <F label="Net received"><Input data-testid="sales-log-input-562" readOnly value={f.net_received} className="bg-[#0a0a0a] border-[#262626] font-mono text-emerald-300" /></F>
        </div>
        <TaxSplit amount={f.paid} rate={taxRate} enabled={taxEnabled} taxName={taxName} fee={f.platform_fee_deducted} testid="sale-tax-split" />

        {isAnnual && (
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.03] p-4" data-testid="split-payment-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300/80">Payment plan · Annual</div>
                <div className="text-xs text-zinc-400 mt-0.5">Full payment now, or split into up to 3 monthly instalments (auto-generated equally).</div>
              </div>
              <div className="inline-flex rounded-full border border-[#262626] bg-[#0a0a0a] p-0.5 text-xs">
                <button type="button" onClick={() => toggleSplit(false)} className={`px-3 py-1 rounded-full transition-colors ${!isSplit ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="payment-type-full">Full payment</button>
                <button type="button" onClick={() => toggleSplit(true)} className={`px-3 py-1 rounded-full transition-colors ${isSplit ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="payment-type-split">Split payment</button>
              </div>
            </div>
            {isSplit && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Number of instalments</Label>
                  <div className="inline-flex rounded-full border border-[#262626] bg-[#0a0a0a] p-0.5 text-xs" data-testid="instalment-count-picker">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setInstalmentCount(n)}
                        className={`px-3 py-1 rounded-full transition-colors ${instalmentCount === n ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
                        data-testid={`instalment-count-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Consecutive months from <span className="text-zinc-300">{new Date(f.date || Date.now()).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>. Amounts split equally — override any row if needed.
                  </div>
                </div>
                <div className="border border-[#1a1a1a] rounded-lg overflow-hidden" data-testid="instalment-ledger">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 bg-[#0d0d0d] border-b border-[#1f1f1f]">
                        <th className="px-3 py-2 font-medium">#</th>
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Mode</th>
                        <th className="px-3 py-2 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(f.split_payments || []).map((sp, i) => (
                        <tr key={sp.id || `split-${i}`} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#141414]" data-testid={`split-row-${i}`}>
                          <td className="px-3 py-2 text-zinc-500 tabular-nums">{i + 1}</td>
                          <td className="px-3 py-2">
                            <DatePicker value={(sp.date || "").slice(0, 10)} onChange={(e) => editInstalment(i, "date", e.target.value)} data-testid={`split-date-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <Select data-testid="sales-log-select-616" value={sp.mode || payModes[0]} onValueChange={(v) => editInstalment(i, "mode", v)}>
                              <SelectTrigger className="bg-[#0a0a0a] border-[#262626] h-8 text-xs" data-testid={`split-mode-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                                {payModes.map((v) => (<SelectItem data-testid="sales-log-select-item-619" key={v} value={v} className="focus:bg-[#1a1a1a]">{v}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input type="number" value={sp.amount || 0} onChange={(e) => editInstalment(i, "amount", e.target.value)} className="bg-[#0a0a0a] border-[#262626] font-mono text-right h-8 text-xs w-32 ml-auto" data-testid={`split-amount-${i}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
        </>)}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {isConsultSale ? (
            <F label="Balance still to pay"><Input readOnly value={f.pending_payment || 0} className="bg-[#0a0a0a] border-[#262626] font-mono text-zinc-400" title="Price on the consult minus payments received" data-testid="sale-pending-consult" /></F>
          ) : !isSplit ? (
            <F label={`Balance still to pay${isAnnual && !pendingManual.current && basePrice > 0 && Number(f.paid) > 0 ? " · auto" : ""}`}><Input type="number" value={f.pending_payment || 0} onChange={(e) => set("pending_payment", Number(e.target.value) || 0)} className="bg-[#0a0a0a] border-[#262626] font-mono" data-testid="sale-pending-input" /></F>
          ) : (
            <F label="Balance still to pay"><Input readOnly value={0} className="bg-[#0a0a0a] border-[#262626] font-mono text-zinc-500" title="Split Annual sales are paid by their instalments" data-testid="sale-pending-locked" /></F>
          )}
          <Toggle data-testid="sales-log-toggle-641" label="Blood test" value={f.blood_test} onChange={(v) => set("blood_test", v)} />
          <Toggle data-testid="sales-log-toggle-642" label="Bills raised" value={f.bills_raised} onChange={(v) => set("bills_raised", v)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F label="Start date"><DatePicker value={(f.start_date || "").slice(0, 10)} onChange={(e) => set("start_date", e.target.value)} data-testid="sale-start-date-input" /></F>
          <F label="End date (auto)"><DatePicker value={(f.end_date || "").slice(0, 10)} onChange={(e) => set("end_date", e.target.value)} data-testid="sale-end-date-input" /></F>
          <F label="Referred by">
            <SearchableSelect data-testid="sales-log-searchable-select-648"
              value={f.referred_by_customer_id || ""}
              onChange={(v) => {
                if (!v) { set("referred_by_customer_id", ""); set("referred_by_name", ""); return; }
                const c = customers.find((x) => x.key === v);
                set("referred_by_customer_id", v);
                set("referred_by_name", c?.name || "");
              }}
              options={customers.map((c) => ({
                value: c.key,
                label: c.name,
                hint: c.phone || "",
              }))}
              placeholder="None"
              emptyLabel="— none —"
              testid="sale-referral-select"
            />
          </F>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Member Manager" required={!isConsultSale && isMembership}>
            <RosterSelect roleType="member_manager" value={f.member_manager || ""} onChange={(v) => set("member_manager", v)} testid="sale-member-manager-input" />
            <div className="text-[10px] text-zinc-500 mt-1">
              {isMembership ? "Mandatory. This person owns the biweekly follow-up with the member post-sale." : "Optional for events, merch and other non-membership sales."}
            </div>
          </F>
          {(isFEConversion || isExisting || category === "add_on") && (
            <F label="Accountability Coach" required={isFEConversion || category === "add_on"}>
              <RosterSelect roleType="accountability_coach" value={f.accountability_coach || ""} onChange={(v) => set("accountability_coach", v)} testid="sale-acc-coach-input" />
              <div className="text-[10px] text-amber-400/80 mt-1" data-testid="fe-conversion-coach-hint">
                {isExisting ? "The coach for their challenge. They join this week's 6WC batch on Accountability."
                  : `Required for ${feRetentionLabel()}: this coach receives the slab-based incentive.`}
              </div>
            </F>
          )}
        </div>
        {isExisting && <ExistingMemberBox preview={pausePreview} sale={mode === "edit" ? initial : null} />}
        {mode === "create" && !isExisting && needsSignoff(f) && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-3 text-xs text-amber-200" data-testid="signoff-needed-note">
            Only the 6 Week Challenge and Annual can be part-paid. This balance goes to the facility head for sign-off.
          </div>
        )}
        {mode === "edit" && initial?.part_pay_signoff && <SignoffPanel sale={initial} canDecide={isManager} />}
        <F label="Remarks"><Textarea data-testid="sales-log-textarea-683" value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" rows={2} /></F>
        {mode === "edit" && initial?.id && !initial?.lead_id && !isSplit && (Number(f.pending_payment) > 0 || hasPayments) && (
          <SalePayments saleId={initial.id} pending={Number(f.pending_payment) || 0} payModes={payModes}
            onChange={(t) => { setHasPayments(true); setF((s) => ({ ...s, ...t })); }} />
        )}
        {mode === "edit" && initial?.id && <BillHistory kind="sale" id={initial.id} />}
        <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:bg-[#0f0f0f] max-sm:border-t max-sm:border-[#1f1f1f]">
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full max-sm:w-full max-sm:h-11" data-testid="sale-submit-btn">{busy ? "Saving…" : mode === "create" ? "Log sale" : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}{required && <span className="text-rose-400 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function SelectBox({ value, onChange, opts, testid, labelOf }) {
  return (
    <Select data-testid="sales-log-select-702" value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{(opts || []).map((o) => <SelectItem data-testid="sales-log-select-item-704" key={o} value={o}>{labelOf ? labelOf(o) : o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Toggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-[#1f1f1f] px-3 py-2.5 bg-[#0d0d0d]">
      <div className="text-xs text-zinc-300">{label}</div>
      <Switch data-testid="sales-log-switch-712" checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}



// Search-as-you-type customer picker for the Retention form. Free-text friendly:
// if nothing matches, the operator can still type a brand-new name and it stays in the field.
// When they pick an existing customer, we auto-fill name + phone + email.
function CustomerAutocomplete({ customers, value, phone, onPickCustomer, onChangeText, testid }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const query = (value || "").trim().toLowerCase();
  const matches = useMemo(() => {
    if (!customers || customers.length === 0) return [];
    // Never suggest — user is editing an already-linked customer via `phone` — skip suggestions when input is empty.
    if (!query) return customers.slice(0, 20);
    return (customers || [])
      .filter((c) => {
        const hay = `${c.name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
        return hay.includes(query);
      })
      .slice(0, 20);
  }, [customers, query]);

  const pick = (c) => {
    onPickCustomer?.(c);
    setOpen(false);
    // Return focus + move cursor to end so user can keep editing if they want.
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Anchor the list to the whole name field. The trigger is only the small search icon,
          and the list takes its width from the anchor (it was icon-wide without this). */}
      <PopoverAnchor asChild>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value || ""}
          onChange={(e) => { onChangeText?.(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="bg-[#0a0a0a] border-[#262626] pr-8"
          placeholder="Start typing a name, phone, or email…"
          data-testid={testid}
          autoComplete="off"
        />
        <PopoverTrigger data-testid="sales-log-popover-trigger-759" asChild>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 p-1 rounded"
            data-testid={testid ? `${testid}-open` : undefined}
            aria-label="Search members"
          >
            <UserSearch className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="p-0 border border-[#262626] bg-[#0f0f0f] text-zinc-100 shadow-xl w-[--radix-popover-trigger-width] min-w-[16rem] max-h-72 flex flex-col"
      >
        <div className="flex items-center gap-2 border-b border-[#1f1f1f] px-3 py-2 text-xs text-zinc-500">
          <Search className="h-3.5 w-3.5" />
          {query ? <span>Matches for &quot;<span className="text-zinc-200">{query}</span>&quot;</span> : <span>Recent members</span>}
          <span className="ml-auto text-[10px] tabular-nums">{matches.length}</span>
        </div>
        <div className="overflow-y-auto py-1">
          {matches.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-500">
              No existing customer matches — keep typing to add a new one.
            </div>
          ) : (
            (matches || []).map((c) => {
              const linked = phone && c.phone === phone;
              return (
                <button
                  key={c.key || c.phone || c.name}
                  type="button"
                  onClick={() => pick(c)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#161616] focus:bg-[#161616] focus:outline-none transition-colors"
                  data-testid={`${testid}-item-${c.phone || c.name}`}
                >
                  <Check className={`h-3.5 w-3.5 shrink-0 ${linked ? "text-emerald-400" : "text-transparent"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 truncate">{c.name || "—"}</div>
                    <div className="text-[10px] text-zinc-500 truncate font-mono">
                      {c.phone || "no phone"}{c.email ? ` · ${c.email}` : ""}{c.visits ? ` · ${c.visits} sale${c.visits === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                  {c.member_manager && <span className="text-[10px] text-blue-300 shrink-0">MM: {c.member_manager}</span>}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function needsSignoff(sale) {
  if (sale.lead_id || (sale.fe_pt_pe || "").toUpperCase() === "FE") return false;
  if ((sale.payment_type || "full") === "split" || /annual/i.test(sale.programme || "")) return false;
  return Number(sale.pending_payment) > 0;
}

function SignoffBadge({ sale }) {
  const so = sale.part_pay_signoff;
  if (!so || !(Number(sale.pending_payment) > 0)) return null;
  const look = { pending: ["Awaiting sign-off", "text-amber-300"], rejected: ["Not approved", "text-rose-300"],
    approved: [`Approved · due ${so.due_date || ""}`, "text-emerald-300/80"] }[so.status] || [so.status, "text-zinc-400"];
  return <span className={`text-[10px] ${look[1]}`} title={so.reason || ""} data-testid={`signoff-badge-${sale.id}`}>{look[0]}</span>;
}

// The facility head's decision on a part payment that should have been paid in full.
function SignoffPanel({ sale, canDecide }) {
  const [so, setSo] = useState(sale.part_pay_signoff);
  const [reason, setReason] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const decide = async (approve) => {
    if (!reason.trim()) return toast.error("Give the reason for your decision");
    if (approve && !due) return toast.error("Pick the date the balance is due by");
    setBusy(true);
    try {
      const r = await api.post(`/sales/${sale.id}/signoff`, { approve, reason, due_date: approve ? due : null });
      setSo(r.data.part_pay_signoff); toast.success(approve ? "Part payment approved" : "Marked not approved: it stays flagged until paid in full");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the decision"); } finally { setBusy(false); }
  };
  const tone = so.status === "approved" ? "border-emerald-500/30" : so.status === "rejected" ? "border-rose-500/30" : "border-amber-500/30";
  return (
    <div className={`rounded-lg border ${tone} p-3 space-y-2 text-xs`} data-testid="signoff-panel">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Part payment sign-off</div>
      <div className="text-zinc-300">
        {so.status === "pending" && <>Balance of {cur}{fmtINR(so.owed)} added by {so.requested_by || "staff"}. Waiting for the facility head.</>}
        {so.status === "approved" && <>Approved by {so.decided_by}: balance due by {so.due_date}. {so.reason}</>}
        {so.status === "rejected" && <>Not approved by {so.decided_by}: {so.reason}. Stays flagged until it's paid in full.</>}
      </div>
      {canDecide && so.status !== "approved" && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="bg-[#0a0a0a] border-[#262626] flex-1 min-w-[10rem]" data-testid="signoff-reason" />
          <div className="w-36"><DatePicker value={due} onChange={(e) => setDue(e.target.value)} placeholder="Due by" data-testid="signoff-due" /></div>
          <Button type="button" size="sm" disabled={busy} onClick={() => decide(true)} className="bg-emerald-600 hover:bg-emerald-500" data-testid="signoff-approve">Approve</Button>
          {so.status === "pending" && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => decide(false)} className="border-rose-500/40 text-rose-300 bg-transparent" data-testid="signoff-reject">Don't approve</Button>}
        </div>
      )}
    </div>
  );
}

function ExistingMemberBox({ preview, sale }) {
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const fmtD = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
  const done = sale?.membership_pause;
  const m = done ? { programme: done.programme, end_date: done.old_end_date, new_end_date: done.new_end_date } : preview?.membership;
  return (
    <div className="rounded-lg border border-blue-500/25 bg-blue-500/[0.04] p-3 text-xs text-zinc-300 space-y-1" data-testid="existing-member-box">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Existing member · 6 Week Challenge</div>
      {preview?.price && <div>Price {cur}{fmtINR(preview.price)} ({preview.discount_pct}% off {cur}{fmtINR(preview.full_price)}). No sales incentive.</div>}
      {m ? (
        <div data-testid="existing-member-pause">
          {done ? "Paused" : "Pauses"} their {m.programme || "membership"} for the six weeks: it ends {fmtD(m.new_end_date)} instead of {fmtD(m.end_date)}. Doesn't use their pause allowance.
        </div>
      ) : (
        <div className="text-zinc-500">No active PIF membership found for this phone, so nothing is paused.</div>
      )}
    </div>
  );
}
